import {
  Copy,
  Loader2,
  MessageSquare,
  RefreshCw,
  Send,
  TriangleAlert,
} from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import {
  ConversationHistory,
  type ConversationHistoryMessage,
} from '@/components/sms-composer/ConversationHistory';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  useCopyTextDraftMutation,
  useCreateCheckInV2Mutation,
  useGenerateTextDraftMutation,
  useGetCheckInV2Query,
  useGetTextThreadContextQuery,
  useMarkTextSentMutation,
} from '@/services/api/treatmentCheckInV2Api';

interface TreatmentTextBetaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  taskId: string;
  clientName: string;
  clientPhone?: string;
}

const workflowLabel: Record<string, string> = {
  drafted: 'Ready to Copy',
  copied: 'Copied',
  marked_sent: 'Marked Sent',
};

export const TreatmentTextBetaModal: React.FC<TreatmentTextBetaModalProps> = ({
  open,
  onOpenChange,
  matterId,
  taskId,
  clientName,
  clientPhone,
}) => {
  const [checkInId, setCheckInId] = React.useState<string | null>(null);
  const [draftText, setDraftText] = React.useState('');
  const [hasAttemptedAutoGenerate, setHasAttemptedAutoGenerate] =
    React.useState(false);
  const [bootstrapError, setBootstrapError] = React.useState<string | null>(null);
  const lastServerTextRef = React.useRef('');

  const [createCheckInV2, { isLoading: isCreatingCheckIn }] =
    useCreateCheckInV2Mutation();
  const [generateTextDraft, { isLoading: isGeneratingDraft }] =
    useGenerateTextDraftMutation();
  const [copyTextDraft, { isLoading: isCopyingDraft }] =
    useCopyTextDraftMutation();
  const [markTextSent, { isLoading: isMarkingSent }] =
    useMarkTextSentMutation();

  const { data: checkIn, isFetching: isFetchingCheckIn } = useGetCheckInV2Query(
    checkInId ? { matterId, checkInId } : ({ matterId: '', checkInId: '' } as const),
    { skip: !open || !checkInId },
  );

  const { data: threadContext, isFetching: isFetchingThread } =
    useGetTextThreadContextQuery(
      checkInId ? { matterId, checkInId } : ({ matterId: '', checkInId: '' } as const),
      { skip: !open || !checkInId },
    );

  React.useEffect(() => {
    let cancelled = false;

    if (!open) {
      setCheckInId(null);
      setDraftText('');
      setHasAttemptedAutoGenerate(false);
      setBootstrapError(null);
      lastServerTextRef.current = '';
    } else if (!checkInId && !isCreatingCheckIn && !bootstrapError) {
      createCheckInV2({
        matterId,
        triggeredByTaskId: taskId,
        channel: 'text',
      })
        .unwrap()
        .then((created) => {
          if (cancelled) return;
          setCheckInId(created.id);
        })
        .catch((error) => {
          if (cancelled) return;
          const message =
            error?.data?.error ||
            error?.data?.message ||
            'Failed to open treatment text beta.';
          setBootstrapError(message);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [
    checkInId,
    createCheckInV2,
    isCreatingCheckIn,
    matterId,
    bootstrapError,
    onOpenChange,
    open,
    taskId,
  ]);

  React.useEffect(() => {
    const serverText =
      checkIn?.text_outreach?.final_text ||
      checkIn?.text_outreach?.suggested_text ||
      '';
    if (!serverText) return;

    setDraftText((current) => {
      if (!current || current === lastServerTextRef.current) {
        return serverText;
      }
      return current;
    });
    lastServerTextRef.current = serverText;
  }, [checkIn]);

  React.useEffect(() => {
    if (
      !open ||
      !checkInId ||
      !checkIn ||
      checkIn.text_outreach?.suggested_text ||
      hasAttemptedAutoGenerate ||
      isGeneratingDraft
    ) {
      return;
    }

    setHasAttemptedAutoGenerate(true);
    generateTextDraft({ matterId, checkInId })
      .unwrap()
      .then((response) => {
        const nextText =
          response.text_outreach?.suggested_text ||
          response.text_outreach?.final_text ||
          '';
        setDraftText(nextText);
        lastServerTextRef.current = nextText;
      })
      .catch((error) => {
        const message =
          error?.data?.error ||
          error?.data?.message ||
          'Failed to generate a treatment text draft.';
        toast.error(message);
      });
  }, [
    checkIn,
    checkInId,
    generateTextDraft,
    hasAttemptedAutoGenerate,
    isGeneratingDraft,
    matterId,
    open,
  ]);

  const workflowState = checkIn?.text_outreach?.workflow_state ?? 'drafted';
  const isMarkedSent = workflowState === 'marked_sent';
  const canMarkSent = Boolean(
    checkIn?.text_outreach?.copy_count && !isMarkedSent,
  );

  const historyMessages = React.useMemo<ConversationHistoryMessage[]>(
    () => threadContext?.messages ?? [],
    [threadContext?.messages],
  );
  const hasConversationHistory = historyMessages.length > 0;

  const handleRegenerate = async () => {
    if (!checkInId || isMarkedSent) return;
    try {
      const response = await generateTextDraft({ matterId, checkInId }).unwrap();
      const nextText =
        response.text_outreach?.suggested_text ||
        response.text_outreach?.final_text ||
        '';
      setDraftText(nextText);
      lastServerTextRef.current = nextText;
      toast.success('Draft refreshed');
    } catch (error) {
      const message =
        (error as { data?: { error?: string; message?: string } })?.data?.error ||
        (error as { data?: { error?: string; message?: string } })?.data?.message ||
        'Failed to refresh draft.';
      toast.error(message);
    }
  };

  const handleCopy = async () => {
    if (!checkInId || !draftText.trim() || isMarkedSent) return;
    try {
      await navigator.clipboard.writeText(draftText.trim());
      const response = await copyTextDraft({
        matterId,
        checkInId,
        finalText: draftText.trim(),
      }).unwrap();
      const nextText =
        response.text_outreach?.final_text ||
        response.text_outreach?.suggested_text ||
        draftText.trim();
      setDraftText(nextText);
      lastServerTextRef.current = nextText;
      toast.success('Text copied');
    } catch (error) {
      const message =
        (error as { data?: { error?: string; message?: string } })?.data?.error ||
        (error as { data?: { error?: string; message?: string } })?.data?.message ||
        'Failed to copy treatment text.';
      toast.error(message);
    }
  };

  const handleMarkSent = async () => {
    if (!checkInId || !canMarkSent) return;
    try {
      await markTextSent({ matterId, checkInId }).unwrap();
      toast.success('Marked as sent');
    } catch (error) {
      const message =
        (error as { data?: { error?: string; message?: string } })?.data?.error ||
        (error as { data?: { error?: string; message?: string } })?.data?.message ||
        'Failed to mark text as sent.';
      toast.error(message);
    }
  };

  const isLoading =
    (!bootstrapError && isCreatingCheckIn) ||
    isFetchingCheckIn ||
    (!threadContext && isFetchingThread) ||
    (!checkIn?.text_outreach?.suggested_text && isGeneratingDraft);

  const handleRetryOpen = () => {
    setBootstrapError(null);
  };

  let modalBody: React.ReactNode;
  if (bootstrapError) {
    modalBody = (
      <div className="flex min-h-[360px] flex-col items-center justify-center gap-4 text-center">
        <div className="max-w-md rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900">
          {bootstrapError}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button onClick={handleRetryOpen}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </div>
    );
  } else if (isLoading) {
    modalBody = (
      <div className="flex min-h-[360px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  } else {
    modalBody = (
      <div
        className={
          hasConversationHistory
            ? 'grid gap-6 lg:grid-cols-[1.1fr_0.9fr]'
            : 'grid gap-6'
        }
      >
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">
              {workflowLabel[workflowState] || 'Ready to Copy'}
            </Badge>
            {checkIn?.text_outreach?.draft_source === 'fallback' && (
              <Badge variant="secondary">Fallback Draft</Badge>
            )}
            <span className="text-xs text-zinc-500">
              {threadContext?.client_name || clientName}
              {(threadContext?.recipient_phone || clientPhone) &&
                ` • ${threadContext?.recipient_phone || clientPhone}`}
            </span>
          </div>

          {checkIn?.text_outreach?.draft_basis_summary && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
              {checkIn.text_outreach.draft_basis_summary}
            </div>
          )}

          {!threadContext?.sender_configured && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <div className="flex items-start gap-2">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  No Finch SMS sender is configured for this firm. That will
                  not block beta copy flow, but native send is not ready yet.
                </span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-zinc-900">
                  Draft text
                </div>
                <div className="text-xs text-zinc-500">
                  Keep it natural, warm, and easy to answer.
                </div>
              </div>
              <div className="text-xs text-zinc-500">
                {draftText.trim().length} chars
              </div>
            </div>
            <Textarea
              value={draftText}
              onChange={(event) => setDraftText(event.target.value)}
              className="min-h-[260px] resize-none text-sm leading-6"
              placeholder="Draft will appear here"
              disabled={isMarkedSent}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={handleRegenerate}
              disabled={
                !checkInId ||
                isGeneratingDraft ||
                isCopyingDraft ||
                isMarkedSent ||
                (checkIn?.text_outreach?.copy_count ?? 0) > 0
              }
            >
              {isGeneratingDraft ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Try Another Draft
            </Button>
            <Button
              onClick={handleCopy}
              disabled={
                !checkInId ||
                !draftText.trim() ||
                isCopyingDraft ||
                isMarkingSent ||
                isMarkedSent
              }
            >
              {isCopyingDraft ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Copy className="mr-2 h-4 w-4" />
              )}
              Copy Text
            </Button>
            <Button
              variant="secondary"
              onClick={handleMarkSent}
              disabled={!canMarkSent || isMarkingSent}
            >
              {isMarkingSent ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Mark Sent
            </Button>
          </div>
        </div>

        {hasConversationHistory && (
          <div className="min-h-[420px]">
            <ConversationHistory
              messages={historyMessages}
              className="h-full min-h-[420px]"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl max-h-[92vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-blue-600" />
            Treatment Text Beta
          </DialogTitle>
          <DialogDescription>
            Draft an initial or follow-up treatment text, copy it into
            Filevine or your SMS tool, then mark it sent here.
          </DialogDescription>
        </DialogHeader>
        {modalBody}
      </DialogContent>
    </Dialog>
  );
};
